import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {Node, XYPosition} from 'reactflow';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  createStaticNodeData,
  createTaskNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import TaskSchemaNodeRenderer from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';
import {SchemaInfo} from 'processes/modals/field-mapping/FieldMappingConstants';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';

import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {generateTaskSchemaNodeIdentifier} from '../flowchartIdentifierUtils';
import {
  FLOWCHART_OUTPUT_SECONDARY_INFO,
  FLOWCHART_TASK_SECONDARY_INFO,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';
import {TaskSchemaNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartOptions} from '../processFlowchartConfigEditorUtils';
import {NodeSidepanelSectionProps} from './NodeSidepanelSection';
import {DEFAULT_NODE_PROPS, DraggableNode} from './sidepanelNodeUtils';
/**
 * Creates a node specifically for the sidepanel.
 * This node is draggable onto the canvas, but does not have sections or field / header interactions.
 */
function createNodeDataForSidepanel(schemaInfo: SchemaInfo, nodeId: string): TaskSchemaNodeData {
  return {
    id: nodeId,
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeName: schemaInfo.taskSchema.name,
    nodeSecondaryInfo: isFlowchartTaskSchema({executionType: schemaInfo.taskSchema.executionType})
      ? schemaInfo.itemType === ModelTypeConstants.PROCESS_TASK
        ? FLOWCHART_TASK_SECONDARY_INFO
        : FLOWCHART_OUTPUT_SECONDARY_INFO
      : undefined,
    sections: [],
    hideSectionSummaries: true,
    onNodeHeaderClick: noop,
    onFieldClick: noop,
    nodeDetailsSidepanelContent: [],
    rowRenderer: () => null,
    nodeHeaderHandleRenderer: () => null,
    taskDefinitionVersionSpec: {
      id: '1',
      newTaskDefinitionVersionData: {
        taskDefinitionId: '1',
        taskSchema: schemaInfo.taskSchema,
        applicationData: null,
      },
    },
    taskSchemaId: schemaInfo.taskSchema.id,
    nodeDetailsType: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
  };
}

function DraggableTaskNode(props: {
  schemaInfo: SchemaInfo;
  nodeId: string;
  addNode: (node: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
}): JSX.Element {
  const addTaskNode = useCallback(
    (position: XYPosition) => {
      const staticNodeData = createStaticNodeData(
        props.schemaInfo,
        props.schemaInfo.taskSchema as ProcessTaskSchemaGraphQL
      );
      const newNode = createTaskNode(staticNodeData, position, props.options);
      props.addNode(newNode);
    },
    [props]
  );

  const nodeData = useMemo(
    () => createNodeDataForSidepanel(props.schemaInfo, props.nodeId),
    [props.schemaInfo, props.nodeId]
  );

  const nodeRenderer = useCallback(
    ({dragging}: {dragging: boolean}) => (
      <TaskSchemaNodeRenderer
        {...DEFAULT_NODE_PROPS}
        id={props.nodeId}
        type={ProcessFlowchartNodeType.TASK_SCHEMA_NODE}
        data={nodeData}
        dragging={dragging}
      />
    ),
    [props.nodeId, nodeData]
  );

  return <DraggableNode addNode={addTaskNode} nodeRenderer={nodeRenderer} />;
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function TaskNodeSidepanelSection(
  props: Omit<NodeSidepanelSectionProps, 'section' | 'nodeTypes'>
): JSX.Element {
  return (
    <div className={css(styles.taskNodesContainer)}>
      {props.availableTaskSchemaInfos
        .filter((schemaInfo) => {
          if (props.searchQuery == null) {
            return true;
          }
          return schemaInfo.taskSchema.name.toLowerCase().includes(props.searchQuery.toLowerCase());
        })
        .map((schemaInfo) => {
          const nodeId = generateTaskSchemaNodeIdentifier(schemaInfo.taskSchema as ProcessTaskSchemaGraphQL);
          return (
            <DraggableTaskNode
              key={nodeId}
              nodeId={nodeId}
              schemaInfo={schemaInfo}
              addNode={props.addNodeToCanvas}
              options={props.options}
            />
          );
        })}
    </div>
  );
}

const styles = StyleSheet.create({
  taskNodesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
});
